/**
 * T049 (catalogue-sku-editor). Structural pattern copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (edit
 * mode — the page-with-back-button + FormGroup field layout) — the same sources ../brands/Detail.tsx
 * cites (specs/017-isomorphic-theme-only/contracts/screen-map.md).
 *
 * `productId`, `code` and `stockingUnitId` are fixed at creation (FR-009) — rendered disabled with
 * the same Lock-icon "Permanent." hint `brands/Detail.tsx` uses for `code`, not as editable fields
 * `UpdateSkuRequest` (which carries none of the three — confirmed against `catalog.d.ts`) would
 * reject.
 *
 * T047 (SKU deactivation control): Deactivate button + `ReasonDialog`, same pattern as
 * `../categories/Detail.tsx`/`../products/Detail.tsx` — `deactivateSku` already existed in
 * `lib/services/catalog.ts`, unused until now.
 */
import { useState } from "react";
import { Link, useLoaderData, useLocation, useNavigate, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { useGoBack } from "../../../lib/navigation";
import { changedSlug, SLUG_HINT, slugSchema } from "../../../lib/slug";
import { skuPath } from "../../../lib/paths";
import {
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiLockDuotone as Lock,
  PiLockKeyDuotone as FixedIcon,
  PiSlidersHorizontalDuotone as Units,
  PiTagDuotone as AttributesIcon,
  PiToggleRightDuotone as StatusIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { RecordSummary } from "../../../components/detail-parts";
import { StatusSwitch } from "../../../components/status-switch";
import { byKey, deactivateSku, getSku, getSkuBySlug, listGstRates, reactivateSku, updateSku, type GstRate } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { Button } from "rizzui";

export async function skuDetailLoader({ params, request }: LoaderFunctionArgs) {
  // Inactive rates are dropped from the dropdown — only the SKU's own current rate is kept even if
  // it's since been deactivated, so saving without touching this field doesn't get rejected for no
  // longer matching any listed option (same "seed the current value" idiom as BrandPicker/CategoryPicker).
  const [sku, allGstRates] = await Promise.all([byKey(request, params.slug!, getSku, getSkuBySlug, skuPath), listGstRates(false)]);
  if (!sku || !allGstRates) throw new Error("The API returned no body for this SKU.");
  const gstRates = { ...allGstRates, items: allGstRates.items.filter((r) => r.isActive || r.id === sku.gstRateId) };
  // Display-only: the fixed fields show the names Catalog sends beside the ids — never the ids.
  const productName = sku.productName ?? "—";
  const stockingUnitCode = sku.stockingUnitCode ?? sku.units.find((u) => u.unitId === sku.stockingUnitId)?.unitCode ?? "—";
  return { sku, gstRates, productName, stockingUnitCode };
}

const skuEditSchema = z.object({
  name: z.string().trim().min(1, "Required").max(200),
  slug: slugSchema,
  hsnCode: z.string().trim().min(1, "Required").max(20),
  // BUG-08: Catalog now owns a GstRate registry (GET /api/catalog/gst-rates) — a real dropdown
  // replaces the free-text id this screen used to accept unvalidated.
  gstRateId: z.string().trim().min(1, "Choose a GST rate"),
  size: z.string().trim().max(50).optional().or(z.literal("")),
  thickness: z.string().trim().max(50).optional().or(z.literal("")),
  grade: z.string().trim().max(50).optional().or(z.literal("")),
  finish: z.string().trim().max(50).optional().or(z.literal("")),
  length: z.string().trim().max(50).optional().or(z.literal("")),
  isActive: z.boolean(),
});
type SkuEditValues = z.infer<typeof skuEditSchema>;

function attributesOrNull(values: SkuEditValues) {
  const attrs = {
    size: values.size || null,
    thickness: values.thickness || null,
    grade: values.grade || null,
    finish: values.finish || null,
    length: values.length || null,
  };
  return Object.values(attrs).some((v) => v !== null) ? attrs : null;
}

/** `readOnly` is the `/view` route (the list's code link): the same screen, every field disabled, no footer. */
export default function SkuDetail({ readOnly = false }: { readOnly?: boolean }) {
  const { sku, gstRates, productName, stockingUnitCode } = useLoaderData() as Awaited<ReturnType<typeof skuDetailLoader>>;
  const goBack = useGoBack("/catalogue/skus");
  const navigate = useNavigate();
  const { search } = useLocation();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [error, setError] = useState<unknown>(null);

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this SKU" />;
  }
  const canWrite = !readOnly && can(permissionState, "catalogue.administer");

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={sku.name}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue · SKUs", href: "/catalogue/skus" }, { name: sku.code }]}
      />

      {/* Code, status and the SKU's own sub-screens in the page; the status itself is edited in the form. */}
      <RecordSummary
        code={sku.code}
        links={
          <>
            <Link to={`${skuPath(sku.slug)}/units`}>
              <Button as="span" variant="outline" size="sm">
                <Units aria-hidden className="me-1.5 size-4" />
                Sellable units
              </Button>
            </Link>
            {can(permissionState, "pricing.read") ? (
              <Link to={`${skuPath(sku.slug)}/base-prices`}>
                <Button as="span" variant="outline" size="sm">
                  Base prices
                </Button>
              </Link>
            ) : null}
          </>
        }
      />

      {error ? <ErrorSurface error={error} action="save this SKU" /> : null}

      <Form<SkuEditValues>
        validationSchema={skuEditSchema}
        useFormProps={{
          defaultValues: {
            name: sku.name,
            slug: sku.slug,
            hsnCode: sku.hsnCode,
            gstRateId: sku.gstRateId,
            size: sku.attributes?.size ?? "",
            thickness: sku.attributes?.thickness ?? "",
            grade: sku.attributes?.grade ?? "",
            finish: sku.attributes?.finish ?? "",
            length: sku.attributes?.length ?? "",
            isActive: sku.isActive,
          },
        }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          // Set once the update has saved a new slug: from then on the old slug's URL reads nothing.
          let savedSlug: string | undefined;
          // A slug change makes the new address the record's only one (there is no slug history), so
          // the page moves there, replacing the old entry rather than leaving it in history.
          const moveTo = (slug: string) => navigate({ pathname: skuPath(slug), search }, { replace: true });
          try {
            const slug = changedSlug(values.slug, sku.slug);
            await updateSku(sku.id, {
              name: values.name,
              hsnCode: values.hsnCode,
              gstRateId: values.gstRateId,
              attributes: attributesOrNull(values),
              slug,
            });
            savedSlug = slug;
            // The status is its own call (deactivate never deletes — FR-007), made only when the box changed.
            if (values.isActive !== sku.isActive) await (values.isActive ? reactivateSku : deactivateSku)(sku.id);
            toast.success("SKU saved");
            if (slug) moveTo(slug);
            else goBack();
          } catch (err) {
            setError(err);
            toast.error("Could not save the SKU");
            // The update went through and only the status call failed: show that on the new address.
            if (savedSlug) moveTo(savedSlug);
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={FixedIcon}>Fixed at creation</SectionTitle>} description="The API rejects a change to any of these — set once, at creation.">
                <Field
                  label="Product"
                  name="productId"
                  defaultValue={productName}
                  readOnly
                  disabled
                  className="font-mono"
                  hint={
                    <span className="flex items-center gap-1.5">
                      <Lock aria-hidden className="size-3" />
                      Permanent.
                    </span>
                  }
                />
                <Field
                  label="Code"
                  name="code"
                  defaultValue={sku.code}
                  readOnly
                  disabled
                  className="font-mono"
                  hint={
                    <span className="flex items-center gap-1.5">
                      <Lock aria-hidden className="size-3" />
                      Permanent.
                    </span>
                  }
                />
                <Field
                  label="Stocking unit"
                  name="stockingUnitId"
                  defaultValue={stockingUnitCode}
                  readOnly
                  disabled
                  className="font-mono"
                 
                  hint={
                    <span className="flex items-center gap-1.5">
                      <Lock aria-hidden className="size-3" />
                      Permanent — see Sellable units for every unit this SKU can be sold in.
                    </span>
                  }
                />
              </FormGroup>

              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>}>
                <Field label="Name" required maxLength={200} disabled={!canWrite} error={errors.name?.message} {...register("name")} />
                <Field
                  label="URL slug"
                  maxLength={200}
                  required
                  className="font-mono"
                  disabled={!canWrite}
                  hint={SLUG_HINT}
                  error={errors.slug?.message}
                  {...register("slug")}
                />
                <Field label="HSN code" required maxLength={20} className="font-mono" disabled={!canWrite} error={errors.hsnCode?.message} {...register("hsnCode")} />
                <SelectField
                  label="GST rate"
                  required
                  disabled={!canWrite || gstRates.items.length === 0}
                  disabledReason={gstRates.items.length === 0 ? "No GST rates available." : undefined}
                 
                  error={errors.gstRateId?.message}
                  {...register("gstRateId")}
                >
                  <option value="">{gstRates.items.length === 0 ? "No GST rates available" : "Choose a GST rate…"}</option>
                  {gstRates.items.map((r: GstRate) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                      {r.isActive ? "" : " (inactive)"}
                    </option>
                  ))}
                </SelectField>
              </FormGroup>

              <FormGroup title={<SectionTitle icon={AttributesIcon}>Attributes</SectionTitle>} description="Optional — whatever applies to this SKU.">
                <Field label="Size" maxLength={50} disabled={!canWrite} error={errors.size?.message} {...register("size")} />
                <Field label="Thickness" maxLength={50} disabled={!canWrite} error={errors.thickness?.message} {...register("thickness")} />
                <Field label="Grade" maxLength={50} disabled={!canWrite} error={errors.grade?.message} {...register("grade")} />
                <Field label="Finish" maxLength={50} disabled={!canWrite} error={errors.finish?.message} {...register("finish")} />
                <Field label="Length" maxLength={50} disabled={!canWrite} error={errors.length?.message} {...register("length")} />
              </FormGroup>

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether this SKU can be ordered.">
                <StatusSwitch
                  className="@xl:col-span-2"
                  disabled={!canWrite}
                  activeText="Can be ordered."
                  inactiveText="Cannot be ordered. Nothing is deleted — existing prices, inventory records and past orders keep referring to it."
                  {...register("isActive")}
                />
              </FormGroup>
            </div>

            {canWrite ? (
              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            ) : null}
          </>
        )}
      </Form>
    </div>
  );
}
